/**
 * New-article companion to `EmbeddedQueriesList`.
 *
 * Drives the same UI affordances (picker, title-override, up/down,
 * remove) but reads/writes from the editor's reducer state instead of
 * the backend. Everything stays in memory until the create POST,
 * which ships the pending list as `embedded_queries: [...]`.
 *
 * Kept as a separate file (rather than dual-mode flag on
 * `EmbeddedQueriesList`) because the data shape, validation surface,
 * and side-effect model are different enough that branching inside
 * one component would obscure both paths. The shared picker
 * (`QueryPickerDialog`) is reused as-is.
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  PendingEmbed,
  UseArticleEditor,
} from "./useArticleEditor";
import QueryPickerDialog from "./QueryPickerDialog";

export interface PendingEmbedsListProps {
  embeds: PendingEmbed[];
  editor: UseArticleEditor;
}

export default function PendingEmbedsList({
  embeds,
  editor,
}: PendingEmbedsListProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const alreadyEmbeddedIds = useMemo(
    () => new Set(embeds.map((e) => e.saved_query_id)),
    [embeds],
  );

  return (
    <Box
      sx={{
        backgroundColor: "#fafafa",
        border: "1px solid #e6e6e6",
        borderRadius: "4px",
        p: 2.5,
        mb: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          Embedded queries
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPickerOpen(true)}
          sx={{ textTransform: "none" }}
        >
          Add query
        </Button>
      </Stack>

      {embeds.length === 0 ? (
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          No embedded queries staged yet. Click <strong>Add query</strong>
          {" "}to pick one — it'll be attached when you save the article.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {embeds.map((embed, idx) => (
            <PendingEmbedRow
              key={embed.localId}
              embed={embed}
              isFirst={idx === 0}
              isLast={idx === embeds.length - 1}
              onMoveUp={() => editor.pendingEmbedMove(embed.localId, -1)}
              onMoveDown={() => editor.pendingEmbedMove(embed.localId, 1)}
              onDelete={() => editor.pendingEmbedRemove(embed.localId)}
              onSaveTitle={(t) =>
                editor.pendingEmbedSetTitle(embed.localId, t)
              }
            />
          ))}
        </Stack>
      )}

      <QueryPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(sq) => {
          editor.pendingEmbedAdd(sq);
          setPickerOpen(false);
        }}
        alreadyEmbeddedIds={alreadyEmbeddedIds}
      />
    </Box>
  );
}

function PendingEmbedRow({
  embed,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onDelete,
  onSaveTitle,
}: {
  embed: PendingEmbed;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onSaveTitle: (titleOverride: string | null) => void;
}) {
  const [draftTitle, setDraftTitle] = React.useState<string>(
    embed.title_override ?? "",
  );
  // Sync local field when the reducer's title changes externally (e.g.,
  // if a future "clear all" affordance ever lands).
  React.useEffect(() => {
    setDraftTitle(embed.title_override ?? "");
  }, [embed.title_override]);

  const commitTitle = () => {
    const normalized = draftTitle.trim() || null;
    if (normalized === (embed.title_override ?? null)) return;
    onSaveTitle(normalized);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #e6e6e6",
        borderRadius: "3px",
        p: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={embed.name}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            helperText={
              draftTitle.trim()
                ? `Override · falls back to "${embed.name}"`
                : `Using saved query name: "${embed.name}"`
            }
            FormHelperTextProps={{
              sx: { fontSize: "0.72rem", color: "text.secondary", ml: 0 },
            }}
          />
        </Box>
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <Tooltip title="Move up" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onMoveUp}
                disabled={isFirst}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onMoveDown}
                disabled={isLast}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove" arrow>
            <IconButton
              size="small"
              onClick={onDelete}
              sx={{ color: "#CF0A2C" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}
